import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Box,
    Alert
} from "@mui/material";
import { AdminPanelSettings as AdminIcon, PersonAdd as PersonAddIcon } from "@mui/icons-material";
import AppHeader from "../../components/AppHeader";

const SystemAccountsPage = ({ setIsLoggedIn }) => {
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });

    const [form, setForm] = useState({
        username: "",
        email: "",
        password: "",
        role: "support_admin"
    });

    const tenantHeader = { "X-Tenant": localStorage.getItem("tenant") };

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/users/system-accounts`, {
                withCredentials: true,
                headers: tenantHeader
            });
            if (res.data.success) setUsers(res.data.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleCreate = async () => {
        setLoading(true);
        setMsg({ type: "", text: "" });
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL1}/users/system-accounts`, form, {
                withCredentials: true,
                headers: tenantHeader
            });
            if (res.data.success) {
                setMsg({ type: "success", text: "Account Created" });
                setOpen(false);
                fetchUsers();
            }
        } catch (err) {
            setMsg({ type: "error", text: err.response?.data?.message || "Failed to create" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f5f5f5" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "#1b5e20", display: "flex", alignItems: "center", gap: 2 }}>
                        <AdminIcon fontSize="large" /> System Accounts
                    </Typography>
                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpen(true)} sx={{ bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
                        New System User
                    </Button>
                </Box>

                {msg.text && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}

                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
                    <Table>
                        <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>Username</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Role</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u) => (
                                <TableRow key={u.id} hover>
                                    <TableCell>{u.username}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <Box sx={{
                                            px: 1, py: 0.5, borderRadius: 1, display: "inline-block",
                                            bgcolor: u.role === "super_admin" ? "#e8f5e9" : "#fff3e0",
                                            color: u.role === "super_admin" ? "#2e7d32" : "#ef6c00",
                                            fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase"
                                        }}>
                                            {u.role.replace("_", " ")}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{u.is_active ? "Active" : "Disabled"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Create Dialog */}
                <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Create System Account</DialogTitle>
                    <DialogContent>
                        <TextField fullWidth label="Username" margin="dense" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                        <TextField fullWidth label="Email" margin="dense" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <TextField fullWidth label="Password" type="password" margin="dense" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                        <TextField fullWidth select label="Role" margin="dense" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            <MenuItem value="support_admin">Support Admin</MenuItem>
                            <MenuItem value="super_admin">Super Admin</MenuItem>
                        </TextField>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCreate} disabled={loading} sx={{ bgcolor: "#2e7d32" }}>
                            Create Account
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default SystemAccountsPage;
